import { flexRender, Table as ReactTable, Row } from '@tanstack/react-table';
import { baseSortTypes } from 'noddi-types';
import { cn } from 'noddi-ui-common';
import { applySessionSearchToHref } from 'noddi-util';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../base/table';
import { useSessionQueryParams } from '../../../../hooks/sessionQueryParamContext';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { noddiToast } from '../../../Feedback/NoddiToaster/toast';
import { NoddiTooltip } from '../../../Overlay/NoddiTooltip';
import {
  resolveTableColumnHeaderTranslations,
  resolveTableContextMenuTranslations,
  resolveTablePaginationTranslations,
  resolveTableRowTranslations,
  TableColumnHeaderTranslations,
  TableContextMenuTranslations,
  TablePaginationTranslations,
  TableRowTranslations
} from '../tableTranslationTypes';
import { BaseTableCommonProps, NoddiColumnMeta, TableAction } from './../types';
import {
  getTableRowCopyId,
  NEW_TAB_WINDOW_FEATURES,
  shouldOpenRowUrlInNewTab,
  shouldShowRowMenu
} from './rowNavigation';
import { TableBodyRow } from './TableBodyRow';
import { TableColumnHeader } from './TableColumnHeader';
import { TableLoadMoreRow } from './TableLoadMoreRow';
import { TableRowContextMenuContent } from './TableRowContextMenuContent';
import { TableSkeleton } from './TableSkeleton';
import { useRowKeyboardNavigation } from './useRowKeyboardNavigation';

interface BaseTableProps<TData, TValue, TOrderingKey extends string = string> {
  table: ReactTable<TData>;
  isPending?: boolean;
  skeletonRowCount?: number;
  emptyStateText?: string;
  containerClassName?: string;
  onRowClick?: BaseTableCommonProps<TData, TValue>['onRowClick'];
  onRowContextMenu?: BaseTableCommonProps<TData, TValue>['onRowContextMenu'];
  getUrlPath?: BaseTableCommonProps<TData, unknown>['getUrlPath'];
  actions?: TableAction<TData>[];
  ordering?: BaseTableCommonProps<TData, TValue, TOrderingKey>['ordering'];
  onOrderChange?: BaseTableCommonProps<TData, TValue, TOrderingKey>['onOrderChange'];
  disableRowMenu?: BaseTableCommonProps<TData, TValue>['disableRowMenu'];
  hideCopyRowId?: BaseTableCommonProps<TData, TValue>['hideCopyRowId'];
  showRowMenuWithSelection?: BaseTableCommonProps<TData, TValue>['showRowMenuWithSelection'];
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  translations?: {
    contextMenu?: Partial<TableContextMenuTranslations>;
    columnHeader?: Partial<TableColumnHeaderTranslations>;
    pagination?: Partial<TablePaginationTranslations>;
    row?: Partial<TableRowTranslations>;
  };
}

export function BaseTable<TData, TValue, TOrderingKey extends string = string>({
  table,
  isPending = false,
  skeletonRowCount = 10,
  emptyStateText = 'No results',
  containerClassName,
  onRowClick,
  onRowContextMenu,
  getUrlPath,
  actions,
  ordering,
  onOrderChange,
  disableRowMenu = false,
  hideCopyRowId = false,
  showRowMenuWithSelection = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  translations
}: BaseTableProps<TData, TValue, TOrderingKey>) {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionQueryParams = useSessionQueryParams();
  const contextMenuTexts = resolveTableContextMenuTranslations(translations?.contextMenu);
  const columnHeaderTexts = resolveTableColumnHeaderTranslations(translations?.columnHeader);
  const paginationTexts = resolveTablePaginationTranslations(translations?.pagination);
  const rowTexts = resolveTableRowTranslations(translations?.row);
  const isMobile = useIsMobile();
  const hasRowSelection = Boolean(table.options.enableRowSelection);
  // A menu with neither "Copy row id" nor actions has nothing to open.
  const rowMenuIsEmpty = hideCopyRowId && !actions?.length;
  const showRowMenu = shouldShowRowMenu({
    disableRowMenu: disableRowMenu || rowMenuIsEmpty,
    hasRowSelection,
    showRowMenuWithSelection,
    hasCustomContextMenu: Boolean(onRowContextMenu)
  });
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const totalColumnCount = visibleColumnCount + (showRowMenu ? 1 : 0);
  const rows = table.getRowModel().rows;
  const { scrollContainerRef, loadMoreSentinelRef } = useInfiniteScroll<HTMLDivElement, HTMLTableRowElement>({
    onLoadMore,
    hasMore,
    isLoadingMore,
    itemCount: rows.length
  });

  const isRowInteractive = (row: TData) => Boolean(getUrlPath?.(row)?.url || onRowClick);
  const interactiveRowIds = rows.filter((row) => isRowInteractive(row.original)).map((row) => row.id);
  const { getRowKeyboardProps, hintId } = useRowKeyboardNavigation(interactiveRowIds);

  const handleRowClick = (row: TData, event: MouseEvent | KeyboardEvent) => {
    if (onRowClick) {
      onRowClick(row);
      return;
    }

    const urlPath = getUrlPath?.(row);

    if (urlPath?.url) {
      if (
        shouldOpenRowUrlInNewTab({
          isExternalUrl: urlPath.isExternalUrl,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey
        })
      ) {
        window.open(
          urlPath.isExternalUrl
            ? urlPath.url
            : applySessionSearchToHref(location.search, urlPath.url, sessionQueryParams),
          '_blank',
          NEW_TAB_WINDOW_FEATURES
        );
      } else {
        navigate(urlPath.url);
      }
    }
  };

  // Non-navigable rows suppress hover highlight without !important so selected
  // state (data-[state=selected]:bg-lightPurple on TableRow) still applies.
  const getRowClassName = (row: TData) => (isRowInteractive(row) ? 'cursor-pointer' : 'hover:bg-transparent');

  const copyRowId = async (row: Row<TData>) => {
    try {
      await navigator.clipboard.writeText(getTableRowCopyId(row));
      noddiToast.success(contextMenuTexts.copiedToClipboard);
    } catch {
      noddiToast.error(contextMenuTexts.failedToCopy);
    }
  };

  // ContextMenuTrigger is on the <tr>; dispatch there so Radix receives the event.
  const handleMenuButtonClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const button = e.currentTarget as HTMLElement;
    const rowElement = button.closest('tr');
    if (!rowElement) {
      return;
    }
    const rect = button.getBoundingClientRect();
    rowElement.dispatchEvent(
      new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
        view: window
      })
    );
  };

  const renderMenuContent = (row: Row<TData>) => (
    <TableRowContextMenuContent
      row={row}
      actions={actions}
      texts={contextMenuTexts}
      onCopyRowId={
        hideCopyRowId
          ? undefined
          : (menuRow) => {
              void copyRowId(menuRow);
            }
      }
    />
  );

  // Adding bg-white as a responsive card background prevents rows from inheriting an unwanted transparent background.
  return (
    <div className='minimal-scrollbar-hover flex h-full flex-col overflow-hidden rounded-md border md:min-w-0'>
      {/* Hidden, yet still the description of every interactive row, which points at it. */}
      <span id={hintId} hidden>
        {rowTexts.keyboardHint}
      </span>
      {/* scroll-pt-9: focus scrolls a row (or its checkbox/menu) clear of the sticky header, not behind it. */}
      <div
        ref={scrollContainerRef}
        className={cn(
          'minimal-scrollbar-hover relative size-full scroll-pt-9 overflow-auto bg-white',
          containerClassName
        )}
      >
        <div style={{ minWidth: 'fit-content' }}>
          <Table style={{ width: '100%' }}>
            <TableHeader className='sticky top-0 z-10 bg-background shadow-sm'>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow className='hover:bg-transparent' key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- columnDef.meta is TanStack's ColumnMeta; the cast is what exposes description, orderingKey and hideSortIcon (TS2339 without it)
                    const columnMeta = header.column.columnDef.meta as NoddiColumnMeta | undefined;
                    const description = columnMeta?.description;
                    const size = header.getSize();
                    const orderColumnKey = columnMeta?.orderingKey as TOrderingKey | undefined;
                    const isColumnSortable = Boolean(orderColumnKey);
                    const matchingOrder =
                      orderColumnKey && isColumnSortable
                        ? ordering?.find((item) => item.column === orderColumnKey)
                        : undefined;
                    const isSortable = Boolean(isColumnSortable && onOrderChange && orderColumnKey);

                    const renderedHeaderContent = flexRender(header.column.columnDef.header, header.getContext());
                    const headerContent = header.isPlaceholder ? null : isSortable &&
                      orderColumnKey &&
                      onOrderChange ? (
                      <TableColumnHeader
                        column={header.column}
                        title={renderedHeaderContent}
                        description={columnMeta?.hideSortIcon ? undefined : description}
                        nativeTitle={
                          columnMeta?.hideSortIcon && typeof description === 'string' ? description : undefined
                        }
                        sortOrder={matchingOrder?.order ?? baseSortTypes.null}
                        isSortable
                        hideSortIcon={columnMeta?.hideSortIcon}
                        onSortChange={(nextOrder) => onOrderChange(orderColumnKey, nextOrder)}
                        translations={columnHeaderTexts}
                      />
                    ) : (
                      <div className='flex items-center gap-1'>
                        {renderedHeaderContent}
                        {description && !isMobile && <NoddiTooltip content={description} />}
                      </div>
                    );

                    return (
                      <TableHead
                        key={header.id}
                        className={cn('bg-background whitespace-nowrap', 'cursor-default')}
                        aria-sort={
                          matchingOrder
                            ? matchingOrder.order === baseSortTypes.asc
                              ? 'ascending'
                              : 'descending'
                            : undefined
                        }
                        style={{
                          width: size,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize
                        }}
                      >
                        {headerContent}
                      </TableHead>
                    );
                  })}
                  {showRowMenu && (
                    <TableHead className='w-12 bg-background p-0' aria-label={contextMenuTexts.openMenu} />
                  )}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isPending ? (
                <TableSkeleton columns={totalColumnCount} rowCount={skeletonRowCount} />
              ) : rows.length ? (
                <>
                  {rows.map((row) => (
                    <TableBodyRow
                      key={row.id}
                      row={row}
                      showRowMenu={showRowMenu}
                      hasRowSelection={hasRowSelection}
                      rowClassName={getRowClassName(row.original)}
                      keyboardProps={getRowKeyboardProps(row.id, (event) => handleRowClick(row.original, event))}
                      openMenuLabel={contextMenuTexts.openMenu}
                      menuContent={showRowMenu ? renderMenuContent(row) : null}
                      onRowClick={handleRowClick}
                      onRowContextMenu={onRowContextMenu}
                      onMenuButtonClick={handleMenuButtonClick}
                    />
                  ))}
                  {onLoadMore && (
                    <TableLoadMoreRow
                      colSpan={totalColumnCount}
                      isLoadingMore={isLoadingMore}
                      sentinelRef={loadMoreSentinelRef}
                      loadingMoreText={paginationTexts.loadingMore}
                    />
                  )}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={totalColumnCount} className='h-24 text-center'>
                    {emptyStateText}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
